import React, { Fragment, memo, useMemo } from 'react';
import { StyledSection, StyledBonusBadge, StyledBonusText, StyledEditContent } from './styled';

import { SECTIONS } from '../../constants/sections';
import { ButtonLegacy, RawHtml, Text, useChat, useRawHtml } from '~/shared/components';
import { useModule } from '~/templates';
import { M200ProfileModule } from '~/lib/data-contract';
import { useAccount, useClub } from '~/features/club';
import { useTranslation } from '~/shared/utils';
import { ProfileDisplay, MemberShip, DeleteUserSection } from '../';
import { Spacer } from '~/theme';
import ChatIcon from '$icons/chat.svg';
import { useUIStateStore } from '../../hooks/useUIStateStore';
import { getStyledElement } from '~/shared/utils/jsx/utils/styled-element';

export const Sections = memo(() => {
    const { sections } = useUIStateStore();
    const {
        element: { advantageSection, bonusSection, helpAndSupportSection, welcomeSection },
    } = useModule<M200ProfileModule>();
    const { account } = useAccount();
    const { translate } = useTranslation();
    const { toggleEditAccountActive, toggleEditPasswordActive } = useClub();
    const { showChat, chatState, chatEnabled } = useChat();

    const nameCapitalized = useMemo(() => {
        return translate('club.profile.welcomeText', {
            name: getStyledElement({
                text: account?.firstname?.toLowerCase(),
                style: { textTransform: 'capitalize' },
            }),
        });
    }, [account?.firstname, translate]);

    const nameMarkup = useRawHtml({ html: nameCapitalized });

    return (
        <>
            {sections.map(({ id, domId, translation }) => (
                <Fragment key={id}>
                    {id === SECTIONS.OVERVIEW && (
                        <StyledSection id={domId}>
                            <Text variant="display1" as="h1">
                                {nameMarkup}
                            </Text>
                            <RawHtml html={welcomeSection} />
                        </StyledSection>
                    )}
                    {id === SECTIONS.BONUS && (
                        <StyledSection id={domId} separated>
                            <Text variant="display2" as="h2">
                                {translation}
                            </Text>
                            <RawHtml html={bonusSection} />
                            <Spacer space="3" />
                            {account?.bonusDisplayPrice && (
                                <StyledBonusBadge>
                                    <Text>{translate('club.profile.bonusBadgeDescription')}</Text>
                                    <StyledBonusText as="strong">
                                        {account.bonusDisplayPrice}
                                    </StyledBonusText>
                                </StyledBonusBadge>
                            )}
                        </StyledSection>
                    )}
                    {id === SECTIONS.ADVANTAGES && (
                        <StyledSection id={domId} separated>
                            <Text variant="display2" as="h2">
                                {translation}
                            </Text>
                            <RawHtml html={advantageSection} />
                        </StyledSection>
                    )}
                    {id === SECTIONS.ACCOUNT && (
                        <StyledSection id={domId} separated>
                            <Text variant="display2" as="h2">
                                {translation}
                            </Text>
                            <StyledEditContent>
                                <ProfileDisplay />
                                <ButtonLegacy
                                    variant="tertiary"
                                    onClick={() => toggleEditAccountActive(true)}
                                >
                                    {translate('general.edit')}
                                </ButtonLegacy>
                            </StyledEditContent>

                            <ButtonLegacy
                                variant="secondary"
                                onClick={() => toggleEditPasswordActive(true)}
                            >
                                {translate('club.edit.editPassword')}
                            </ButtonLegacy>
                        </StyledSection>
                    )}
                    {id === SECTIONS.NEWSLETTER && (
                        <StyledSection id={domId} separated>
                            <Text variant="display2" as="h2">
                                {translation}
                            </Text>
                            <MemberShip />
                        </StyledSection>
                    )}
                    {id === SECTIONS.HELP && (
                        <StyledSection id={domId} separated>
                            <Text variant="display2" as="h2">
                                {translation}
                            </Text>
                            <RawHtml html={helpAndSupportSection} />
                            {chatEnabled && (
                                <>
                                    <Spacer space="3" />
                                    <ButtonLegacy
                                        onClick={showChat}
                                        variant="secondary"
                                        disabled={chatState === 'loading'}
                                    >
                                        <ChatIcon />
                                        {translate('general.chatButtonText')}
                                    </ButtonLegacy>

                                    <Spacer space="3" />
                                    <Text
                                        variant="caption"
                                        children={translate('general.chatCaption')}
                                    />
                                </>
                            )}
                        </StyledSection>
                    )}
                </Fragment>
            ))}
            <StyledSection>
                <DeleteUserSection />
            </StyledSection>
        </>
    );
});
